import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UserService } from '../../../../_services/users.service';
import { MatDialog } from '@angular/material/dialog';
import { StockHistoryComponent } from '../../../inventory/stock-history/stock-history.component';
import { MainLayoutComponent } from '../../../../_layouts/main-layout/main-layout.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { OrderService } from '../../../../_services/order.service';
import { ProductService, ProductPickerItem } from '../../../../_services/product.service';
import { DeliveryPartner, DeliveryPartnersService } from '../../../../_services/delivery-partners.service';
import { AlertService } from '../../../../_services/alert.service';
import { DeliveryQueueParams, DeliveryValidationService, ProductDimensions } from '../../../../_services/delivery-validation.service';
import { OrderDeliveryValidation } from '../../../../_interfaces/delivery-validation';
import { DeliverySlipPdfService } from '../../../../_services/delivery-slip-pdf.service';
import { computeDispatchDate, dispatchUrgencyClass, formatDeliveryTerms } from '../../dispatch-date';
import { DISPATCH_GROUPS, DispatchGroupOption, dispatchGroupOption } from '../../dispatch-groups';
import { OrderDispatchGroup } from '../../../../_interfaces/dispatch-group';
import { ProductPickerComponent } from '../../../../../Shared/_components/product-picker/product-picker.component';

interface SaveConfirmation {
  row: ValidationRow;
  tone: 'red' | 'amber' | 'neutral';
  icon: string;
  title: string;
  actionLabel: string;
  effects: { icon: string; text: string }[];
}

interface ValidationRow {
  item: any;
  validation: OrderDeliveryValidation;
  editValidatedQty: number;
  editCondition: string;
  editCancel: boolean;
  // True when Partial was picked for the user because they checked fewer
  // units than ordered (so it can flip back if the quantity is restored).
  autoPartial?: boolean;
  editNotes: string;
  saving: boolean;
  justSaved: boolean;
  dimensions: ProductDimensions | null;
}

@Component({
    selector: 'app-order-validation-detail',
    imports: [
        CommonModule,
        FormsModule,
        MainLayoutComponent,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatMenuModule,
        DatePipe,
        RouterLink,
        ProductPickerComponent,
    ],
    templateUrl: './order-validation-detail.component.html',
    styleUrl: './order-validation-detail.component.css'
})
export class OrderValidationDetailComponent implements OnInit {
  orderId!: number;
  order: any = null;
  rows: ValidationRow[] = [];
  loading = false;
  notFound = false;
  conditionOptions: { value: string; label: string; icon: string }[] = [
    { value: 'GOOD', label: 'Good', icon: 'check_circle' },
    { value: 'DAMAGED', label: 'Damaged', icon: 'warning' },
    { value: 'PARTIAL', label: 'Partial', icon: 'remove_circle' },
  ];

  // Same order the queue lists (all PROCESSING orders) - lets Previous/Next
  // step through it without needing to go back to the queue page each time.
  queueOrders: { orderId: number; orderNo: string }[] = [];

  dispatchGroupOptions = DISPATCH_GROUPS;
  dispatchGroup: OrderDispatchGroup | null = null;

  // Manual order-status control for the dispatch team - reuses the same
  // PUT /orders/update/order-details/:id the Sales edit page's status
  // select already calls, just exposed here since that page deliberately
  // hides its own status select for PROCESSING orders (see
  // orderStatusToAvoidUpdate in edit-sale.component.ts) and this is the
  // one place that's meant to close them out. 'DELAYED COMPLETION' (with
  // the space) is the real Orders.orderStatus enum value - not the same
  // thing as the Delayed Dispatch *dispatch group*, which only flags an
  // order for the queue tabs and doesn't touch orderStatus.
  orderStatusOptions: { value: string; label: string; icon: string; badgeClass: string }[] = [
    { value: 'COMPLETED', label: 'Completed', icon: 'check_circle', badgeClass: 'bg-green-100 text-green-700' },
    { value: 'DELAYED COMPLETION', label: 'Delayed Dispatch', icon: 'report_problem', badgeClass: 'bg-orange-100 text-orange-700' },
    { value: 'VOID', label: 'Void', icon: 'block', badgeClass: 'bg-red-100 text-red-700' },
  ];
  updatingOrderStatus = false;

  // Nudges the team to close the order out the moment the last item gets
  // checked off, instead of leaving it sitting fully-validated but still
  // PROCESSING until someone remembers to open the Order Status menu
  // separately. Shown once per page load (dismissing it, or acting on it,
  // both suppress it for the rest of this visit).
  showStatusPrompt = false;
  closeOutRows: ValidationRow[] = [];
  // Delivery partner taking the order out - picked in the close-out prompt,
  // required unless it's a collection.
  deliveryPartner: DeliveryPartner | null = null;
  partners: DeliveryPartner[] = [];
  partnersLoading = false;
  partnersError = '';
  selectedPartnerId: number | null = null;
  // Close-out prompt: send the customer the "order is ready" email (ticked by default).
  emailCustomer = true;
  statusNote = '';

  get orderStatusBadgeClass(): string {
    const opt = this.orderStatusOptions.find((o) => o.value === this.order?.orderStatus);
    return opt?.badgeClass || 'bg-amber-100 text-amber-700';
  }

  get orderStatusIcon(): string {
    const opt = this.orderStatusOptions.find((o) => o.value === this.order?.orderStatus);
    return opt?.icon || 'inventory_2';
  }

  // Once the order has moved past PROCESSING (Completed/Void/Delayed
  // Completion), the pick/pack checklist is history, not a live task -
  // editing quantities/condition/notes or re-saving a row no longer means
  // anything, so the whole item list becomes view-only. Order Status and
  // Dispatch Group stay editable since those are administrative
  // corrections, not part of "validating what's being packed".
  get isReadOnly(): boolean {
    return !!this.order && this.order.orderStatus !== 'PROCESSING';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private deliveryValidationService: DeliveryValidationService,
    private deliverySlipPdfService: DeliverySlipPdfService,
    private productService: ProductService,
    private alertService: AlertService,
    private userService: UserService,
    private dialog: MatDialog,
    private deliveryPartnersService: DeliveryPartnersService,
  ) {}

  // The sales page is for sales staff and admins - delivery-only accounts
  // don't get the link.
  get canOpenSale(): boolean {
    const roles = String(this.userService.isLoggedIn()?.userrole ?? '').split(',').map((r) => r.trim());
    return ['SALES', 'ADMIN', 'SALEADMIN', 'MARKETING', 'S-ADMIN'].some((r) => roles.includes(r));
  }

  ngOnInit(): void {
    // /delivery-validation/<order no>: the number is turned into the order's
    // id (already known when it was opened from the list or Previous/Next).
    this.route.paramMap.subscribe((params) => {
      const orderNo = params.get('orderNo');
      if (!orderNo) {
        this.notFound = true;
        return;
      }
      this.loading = true;
      this.notFound = false;
      this.deliveryValidationService.resolveOrderId(orderNo).subscribe({
        next: (id) => {
          this.orderId = id;
          this.load();
        },
        error: () => {
          this.loading = false;
          this.notFound = true;
        },
      });
    });
  }

  // The list Previous / Next move through: the tab and filters the order was
  // opened from. If that doesn't contain this order (opened from a link, or
  // its status has since moved it to another tab), fall back to the tab its
  // current status belongs to.
  queueTabLabel = '';

  private static readonly TAB_BY_STATUS: Record<string, DeliveryQueueParams['tab']> = {
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    'DELAYED COMPLETION': 'DELAYED',
    VOID: 'VOID',
    CANCELLED: 'CANCELLED',
  };

  private static readonly TAB_LABELS: Record<string, string> = {
    PROCESSING: 'To validate',
    COMPLETED: 'Completed',
    DELAYED: 'Delayed dispatch',
    VOID: 'Void',
    CANCELLED: 'Cancelled',
  };

  private loadQueueOrderRefs(order: any) {
    if (this.queuePosition !== -1) return; // already inside the list we have
    const context = this.deliveryValidationService.queueContext;
    const fallbackTab = OrderValidationDetailComponent.TAB_BY_STATUS[order?.orderStatus];
    const fallback: DeliveryQueueParams | null = fallbackTab
      ? { tab: fallbackTab, pageIndex: 0, pageSize: 0 }
      : null;

    const fetch = (params: DeliveryQueueParams, next?: () => void) =>
      this.deliveryValidationService.getQueueOrderRefs(params).subscribe({
        next: (refs) => {
          this.queueOrders = refs ?? [];
          this.queueTabLabel = OrderValidationDetailComponent.TAB_LABELS[params.tab ?? ''] ?? '';
          refs?.forEach((r) => this.deliveryValidationService.rememberOrder(r.orderId, r.orderNo));
          if (this.queuePosition === -1 && next) next();
        },
        // Non-fatal: Previous/Next just stay disabled.
        error: () => (this.queueOrders = []),
      });

    if (context) {
      fetch(context, () => fallback && fetch(fallback));
    } else if (fallback) {
      fetch(fallback);
    } else {
      this.queueOrders = [];
    }
  }

  get queuePosition(): number {
    return this.queueOrders.findIndex((o) => o.orderId === this.orderId);
  }

  get previousOrderId(): number | null {
    const pos = this.queuePosition;
    return pos > 0 ? this.queueOrders[pos - 1].orderId : null;
  }

  get nextOrderId(): number | null {
    const pos = this.queuePosition;
    return pos !== -1 && pos < this.queueOrders.length - 1 ? this.queueOrders[pos + 1].orderId : null;
  }

  goToPrevious() {
    const pos = this.queuePosition;
    if (pos > 0) this.router.navigate(['/delivery-validation', this.queueOrders[pos - 1].orderNo]);
  }

  goToNext() {
    const pos = this.queuePosition;
    if (pos !== -1 && pos < this.queueOrders.length - 1) {
      this.router.navigate(['/delivery-validation', this.queueOrders[pos + 1].orderNo]);
    }
  }

  get validatedCount(): number {
    return this.rows.filter((r) => r.validation.status === 'VALIDATED' || r.validation.status === 'PARTIAL').length;
  }

  get discrepancyCount(): number {
    return this.rows.filter((r) => r.validation.status === 'DISCREPANCY').length;
  }

  get cancelledCount(): number {
    return this.rows.filter((r) => r.validation.status === 'CANCELLED').length;
  }

  get progressPercent(): number {
    if (!this.rows.length) return 0;
    const done = this.rows.filter((r) => r.validation.status !== 'PENDING').length;
    return Math.round((done / this.rows.length) * 100);
  }

  get customerName(): string {
    const address = (this.order?.OrderAddresses ?? [])[0];
    if (!address) return '-';
    return `${address.firstName ?? ''} ${address.lastName ?? ''}`.trim() || '-';
  }

  get shippingAddress(): any {
    const addresses = this.order?.OrderAddresses ?? [];
    return addresses.find((a: any) => a.addressType === 'SHIPPING') || addresses[0] || null;
  }

  // Orders.deliveryMethod is stored as a fixed code (see the same mapping
  // in OrdersService's Odoo sync) - not deliveryType, which is a separate,
  // unrelated field.
  private static readonly DELIVERY_METHOD_LABELS: Record<string, string> = {
    STANDARD: 'Standard',
    EXPRESS: 'Express',
    NEXTDAY: 'Next Day',
    COLLECTION: 'Collection',
  };

  get deliveryMethodLabel(): string {
    const method = this.order?.deliveryMethod;
    return OrderValidationDetailComponent.DELIVERY_METHOD_LABELS[method] || method;
  }

  get deliveryMethodIcon(): string {
    return this.order?.deliveryMethod === 'COLLECTION' ? 'storefront' : 'local_shipping';
  }

  get deliveryTermsShort(): string | null {
    return formatDeliveryTerms(this.order?.deliveryTerms);
  }

  get dispatchDate(): Date | null {
    return this.order ? computeDispatchDate(this.order) : null;
  }

  get dispatchUrgencyClass(): string {
    return dispatchUrgencyClass(this.dispatchDate);
  }

  get dispatchGroupOption(): DispatchGroupOption | null {
    return dispatchGroupOption(this.dispatchGroup?.dispatchGroup);
  }

  private loadDispatchGroup() {
    this.deliveryValidationService.getDispatchGroup(this.orderId).subscribe({
      next: (group) => (this.dispatchGroup = group),
      error: () => (this.dispatchGroup = null),
    });
  }

  assignDispatchGroup(value: string) {
    this.deliveryValidationService.setDispatchGroup(this.orderId, value).subscribe({
      next: (group) => {
        this.dispatchGroup = group;
        this.alertService.alert('Dispatch group updated', 'success');
      },
      error: (error) => {
        this.alertService.alert(error?.error?.message || 'Failed to update dispatch group', 'error');
      },
    });
  }

  // `note`, when given, is recorded as its own order note after the status
  // change succeeds - e.g. the reason typed into the Void confirmation.
  updateOrderStatus(value: string, note?: string, notifyCustomer?: boolean) {
    this.updatingOrderStatus = true;
    this.orderService.updateOrderStatus(this.orderId, value, notifyCustomer).subscribe({
      next: () => {
        this.order.orderStatus = value;
        this.updatingOrderStatus = false;
        this.alertService.alert(`Order marked as ${value}`, 'success');
        if (note?.trim()) {
          this.orderService
            // Titled so notes coming from the delivery team are easy to
            // tell apart from sales/admin notes in the order's note list.
            .createOrderNote({ orderId: this.orderId, orderNote: `Delivery Notes\n${note.trim()}` })
            .subscribe({
              next: () => this.loadDeliveryNotes(),
              error: () => {}, // Status change already succeeded either way.
            });
        }
      },
      error: (error) => {
        this.updatingOrderStatus = false;
        this.alertService.alert(error?.error?.message || 'Failed to update order status', 'error');
      },
    });
  }

  // Completed/Delayed Dispatch unlock only once the order is ready to
  // close out (canCloseOut) - jumping straight there would let someone
  // close an order that was never actually validated, defeating the point
  // of this whole screen. Void has no such prerequisite, but always needs
  // a reason on record.
  showVoidPrompt = false;
  voidReason = '';

  openVoidPrompt(): void {
    this.voidReason = '';
    this.showVoidPrompt = true;
  }

  confirmVoid(): void {
    if (!this.voidReason.trim()) {
      this.alertService.alert('Enter a reason for voiding this order', 'error');
      return;
    }
    this.showVoidPrompt = false;
    this.updateOrderStatus('VOID', this.voidReason);
  }

  cancelVoidPrompt(): void {
    this.showVoidPrompt = false;
  }

  private load() {
    this.loading = true;
    this.notFound = false;
    this.orderService.getOrderById(this.orderId).subscribe({
      next: (order: any) => {
        if (!order) {
          this.loading = false;
          this.notFound = true;
          return;
        }
        this.order = order;
        this.loadQueueOrderRefs(order);
        this.loadValidationRows(order);
        this.loadDispatchGroup();
        this.loadDeliveryNotes();
        this.loadDeliveryPartner();
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  // Notes the delivery team left (saved with a "Delivery Notes" title line -
  // see updateOrderStatus), shown on this page so the outcome of the
  // delivery is visible without opening the sales order.
  deliveryNotes: { text: string; date: string; user: string }[] = [];

  // ---- Delivery notes: quick add ----
  newDeliveryNote = '';
  savingDeliveryNote = false;

  addDeliveryNote() {
    const text = this.newDeliveryNote.trim();
    if (!text || this.savingDeliveryNote) return;
    this.savingDeliveryNote = true;
    // Same "Delivery Notes" title line the status prompts use, so it shows up
    // here and highlighted on the sales order.
    this.orderService.createOrderNote({ orderId: this.orderId, orderNote: `Delivery Notes\n${text}` }).subscribe({
      next: () => {
        this.savingDeliveryNote = false;
        this.newDeliveryNote = '';
        this.loadDeliveryNotes();
        this.alertService.alert('Delivery note added', 'success');
      },
      error: (error) => {
        this.savingDeliveryNote = false;
        this.alertService.alert(error?.error?.message || 'Could not add the note', 'error');
      },
    });
  }

  // ---- To pack ----
  private get activeRows(): ValidationRow[] {
    return this.rows.filter((r) => r.validation.status !== 'CANCELLED');
  }

  get activeLineCount(): number {
    return this.activeRows.length;
  }

  get unitsToPack(): number {
    return this.activeRows.reduce((sum, r) => sum + (Number(r.validation.expectedQty) || 0), 0);
  }

  get totalWeightKg(): number {
    return this.activeRows.reduce(
      (sum, r) => sum + (Number(r.dimensions?.weight) || 0) * (Number(r.validation.expectedQty) || 0),
      0,
    );
  }

  // ---- Original order card ----
  get originalUnits(): number {
    return (this.order?.OrderItems ?? []).reduce((sum: number, i: any) => sum + (Number(i.quantity) || 0), 0);
  }

  // How far each originally ordered item has got on the delivery lines
  // (a substitute or remaining line counts towards the item it came from).
  itemProgress(item: any): { state: 'done' | 'partial' | 'cancelled' | 'pending'; label: string } {
    const lines = this.rows.filter((r) => r.item?.id === item.id);
    const ordered = Number(item.quantity) || 0;
    const checked = lines
      .filter((r) => r.validation.status === 'VALIDATED' || r.validation.status === 'PARTIAL')
      .reduce((sum, r) => sum + (Number(r.validation.validatedQty) || 0), 0);
    const cancelled = lines
      .filter((r) => r.validation.status === 'CANCELLED')
      .reduce((sum, r) => sum + (Number(r.validation.expectedQty) || 0), 0);
    if (checked >= ordered && ordered > 0) return { state: 'done', label: `${checked}/${ordered} checked` };
    if (checked > 0) return { state: 'partial', label: `${checked}/${ordered} checked` };
    if (cancelled >= ordered && ordered > 0) return { state: 'cancelled', label: 'Cancelled' };
    return { state: 'pending', label: `0/${ordered} checked` };
  }

  // ---- Address helpers ----
  addressText(a: any): string {
    return [a?.companyName, a?.address1, a?.address2, a?.city, a?.postCode, a?.country].filter(Boolean).join(', ');
  }

  mapsUrl(a: any): string {
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(this.addressText(a));
  }

  async copyText(text: string | null | undefined, label: string) {
    try {
      await navigator.clipboard.writeText(String(text ?? ''));
      this.alertService.alert(`${label} copied`, 'success');
    } catch {
      this.alertService.alert('Could not copy - your browser blocked it', 'error');
    }
  }

  // One-click "this line matches the order": checked qty = ordered, good.
  matchRow(row: ValidationRow) {
    row.editCancel = false;
    row.autoPartial = false;
    row.editValidatedQty = row.validation.expectedQty;
    row.editCondition = 'GOOD';
  }

  private loadDeliveryNotes() {
    this.orderService.getOrderNotes(this.orderId).subscribe({
      next: (notes: any) => {
        this.deliveryNotes = (Array.isArray(notes) ? notes : [])
          .filter((n: any) => typeof n.orderNote === 'string' && n.orderNote.startsWith('Delivery Notes\n'))
          .map((n: any) => ({
            text: n.orderNote.substring('Delivery Notes\n'.length),
            date: n.orderNoteDate,
            user: n.orderNoteUser?.firstname ?? '',
          }));
      },
      // Non-fatal: the card just doesn't show.
      error: () => (this.deliveryNotes = []),
    });
  }

  private loadValidationRows(order: any, onLoaded?: () => void) {
    this.deliveryValidationService.getOrCreateForOrder(order.orderId).subscribe({
      next: (validations) => {
        // One row per validation line - an order item can have several
        // once a partial/damaged save has split it.
        const itemsById = new Map<number, any>((order.OrderItems ?? []).map((i: any) => [i.id, i]));
        this.rows = validations
          .map((validation) => {
            const item = itemsById.get(validation.orderItemId);
            if (!item) return null;
            return {
              item,
              validation,
              editValidatedQty: validation.validatedQty ?? validation.expectedQty,
              editCondition: validation.condition ?? 'GOOD',
              editCancel: validation.status === 'CANCELLED',
              editNotes: validation.notes ?? '',
              saving: false,
              justSaved: false,
              dimensions: null,
            } as ValidationRow;
          })
          .filter((row: ValidationRow | null): row is ValidationRow => !!row);
        this.loading = false;
        this.loadDimensions();
        onLoaded?.();
      },
      error: (error) => {
        this.loading = false;
        this.alertService.alert(error?.error?.message || 'Failed to load validation rows', 'error');
      },
    });
  }

  // OrderItems only carries weight, not L/W/H - those live on the product
  // variant, fetched in one bulk call for every item on the order.
  private loadDimensions() {
    const variantIds = [...new Set(this.rows.map((r) => r.validation.substituteVariantId || r.item.variantId).filter(Boolean))];
    if (!variantIds.length) return;
    this.deliveryValidationService.getProductDimensions(variantIds).subscribe({
      next: (dimensions) => {
        const byVariantId = new Map(dimensions.map((d) => [d.productVariantId, d]));
        this.rows.forEach((row) => {
          row.dimensions = byVariantId.get(row.validation.substituteVariantId || row.item.variantId) || null;
        });
      },
      // Non-fatal: the cards just don't show dimensions.
      error: () => {},
    });
  }

  adjustQty(row: ValidationRow, delta: number) {
    const next = (row.editValidatedQty ?? 0) + delta;
    this.onQtyChange(row, Math.max(0, next));
  }

  // Sending fewer than ordered is a partial dispatch - pick Partial for
  // them rather than leaving Good selected against a short quantity, and
  // put it back if the quantity is restored. Damaged/Cancel are left
  // alone (a damaged count isn't a shortfall).
  onQtyChange(row: ValidationRow, value: number | string | null) {
    const qty = Number(value);
    row.editValidatedQty = qty;
    const expected = row.validation.expectedQty;
    if (row.editCancel) return;
    if (row.editCondition === 'GOOD' && qty > 0 && qty < expected) {
      row.editCondition = 'PARTIAL';
      row.autoPartial = true;
    } else if (row.autoPartial && row.editCondition === 'PARTIAL' && (qty >= expected || qty <= 0)) {
      row.editCondition = 'GOOD';
      row.autoPartial = false;
    }
  }

  setCondition(row: ValidationRow, condition: string) {
    row.editCondition = condition;
    row.autoPartial = false;
    // Good/Damaged/Partial/Cancel are one exclusive choice.
    row.editCancel = false;
  }

  // Marks the item as not shipping - takes effect on Save, like every
  // other edit on the row; toggling back and saving restores normal
  // quantity-based validation.
  toggleCancel(row: ValidationRow) {
    row.editCancel = !row.editCancel;
  }

  saveRow(row: ValidationRow) {
    if (row.editValidatedQty === null || row.editValidatedQty === undefined) {
      this.alertService.alert('Enter the quantity you checked', 'error');
      return;
    }
    const confirmation = this.saveConfirmation(row);
    if (confirmation) {
      this.pendingConfirmation = confirmation;
      return;
    }
    if (this.wouldCloseOut([row])) {
      this.openCloseOutPrompt([row]);
      return;
    }
    this.doSave(row);
  }

  pendingConfirmation: SaveConfirmation | null = null;

  // "Add delivery line": a substitute product for a pending line - the
  // ordered item couldn't go out (e.g. out of stock) and the customer
  // agreed to something else instead.
  showAddLine = false;
  addLineKey: string | null = null;
  replacedQty = 1;
  subQty = 1;
  subWarehouse = 'Markstey';
  subNotes = '';
  subSearch = '';
  subProduct: ProductPickerItem | null = null;
  addingLine = false;
  pickerProducts: ProductPickerItem[] = [];

  // What can be substituted: every pending line, plus any ordered item
  // that has no pending line left (already dispatched/cancelled, or
  // nothing outstanding) - those can still get a substitute line, they
  // just have no pending units to cancel in exchange.
  get replaceOptions(): { key: string; orderItemId: number; lineId: number | null; label: string; pending: number; name: string }[] {
    const options: { key: string; orderItemId: number; lineId: number | null; label: string; pending: number; name: string }[] = [];
    const seenItems = new Set<number>();
    for (const r of this.rows) {
      seenItems.add(r.item.id);
      if (r.validation.status === 'PENDING') {
        options.push({
          key: `line-${r.validation.id}`,
          orderItemId: r.item.id,
          lineId: r.validation.id,
          label: `${this.rowProductName(r)} (${r.validation.expectedQty} pending)`,
          pending: r.validation.expectedQty,
          name: this.rowProductName(r),
        });
      }
    }
    const itemsWithPending = new Set(this.rows.filter((r) => r.validation.status === 'PENDING').map((r) => r.item.id));
    for (const itemId of seenItems) {
      if (itemsWithPending.has(itemId)) continue;
      const item = this.rows.find((r) => r.item.id === itemId)!.item;
      options.push({
        key: `item-${itemId}`,
        orderItemId: itemId,
        lineId: null,
        label: `${item.productName} (nothing pending)`,
        pending: 0,
        name: item.productName,
      });
    }
    return options;
  }

  get addLineOption() {
    return this.replaceOptions.find((o) => o.key === this.addLineKey) ?? null;
  }

  // Shown before anything is typed: products sharing the most words with
  // the item being replaced (same stone/colour first), in stock first.
  subSuggestions: ProductPickerItem[] = [];

  private refreshSubSuggestions() {
    const opt = this.addLineOption;
    const row = opt ? this.rows.find((r) => r.item.id === opt.orderItemId) : undefined;
    const variantId = row ? row.validation.substituteVariantId || row.item.variantId : null;
    this.subSuggestions = this.findSimilarProducts(opt?.name, variantId);
  }

  private findSimilarProducts(name: string | undefined, excludeVariantId: number | null): ProductPickerItem[] {
    if (!name || !this.pickerProducts.length) return [];
    const words = (s: string) => s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);
    const target = new Set(words(name));
    const stock = (p: ProductPickerItem) => p.mStockQty ?? 0;
    return this.pickerProducts
      .filter((p) => p.productVariantId && p.productVariantId !== excludeVariantId)
      .map((p) => ({ p, score: words(p.productName).filter((w) => target.has(w)).length }))
      .filter((s) => s.score >= 2)
      .sort((a, b) => b.score - a.score || Number(stock(b.p) > 0) - Number(stock(a.p) > 0))
      .slice(0, 30)
      .map((s) => s.p);
  }

  rowProductName(row: ValidationRow): string {
    return row.validation.substituteName || row.item.productName;
  }

  // Every stock movement for this line's product (the substitute, if one was sent).
  openStockHistory(row: ValidationRow) {
    const productVariantId = row.validation.substituteVariantId || row.item.variantId;
    if (!productVariantId) return;
    this.dialog.open(StockHistoryComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: {
        productVariantId,
        productName: this.rowProductName(row),
        sku: this.rowProductSku(row),
      },
    });
  }

  rowProductSku(row: ValidationRow): string {
    return row.validation.substituteSku || row.item.productSku;
  }

  openAddLine() {
    const first = this.replaceOptions[0];
    if (!first) {
      this.alertService.alert('This order has no items', 'error');
      return;
    }
    this.addLineKey = first.key;
    this.onAddLineRowChange();
    this.subNotes = '';
    this.subSearch = '';
    this.subProduct = null;
    this.subWarehouse = 'Markstey';
    this.showAddLine = true;
    if (!this.pickerProducts.length) {
      this.productService.getProductPicker().subscribe({
        next: (products) => {
          this.pickerProducts = products;
          this.refreshSubSuggestions();
        },
        error: () => this.alertService.alert('Could not load products', 'error'),
      });
    }
  }

  onAddLineRowChange() {
    const opt = this.addLineOption;
    this.replacedQty = opt?.pending ?? 0;
    this.subQty = Math.max(1, opt?.pending ?? 1);
    this.refreshSubSuggestions();
  }

  selectSubProduct(product: ProductPickerItem) {
    this.subProduct = product;
    this.subSearch = product.productName;
    this.subWarehouse = (product.mStockQty ?? 0) >= this.subQty ? 'Markstey' : 'Kettering';
  }

  onSubSearchInput() {
    this.subProduct = null;
  }

  get subStockAtWarehouse(): number | null {
    if (!this.subProduct) return null;
    return (this.subWarehouse === 'Markstey' ? this.subProduct.mStockQty : this.subProduct.kStockQty) ?? 0;
  }

  confirmAddLine() {
    const opt = this.addLineOption;
    if (!opt) return;
    if (!this.subProduct?.productVariantId) {
      this.alertService.alert('Choose the substitute product', 'error');
      return;
    }
    if (opt.lineId && (this.replacedQty < 0 || this.replacedQty > opt.pending)) {
      this.alertService.alert(`Units replaced must be between 0 and ${opt.pending}`, 'error');
      return;
    }
    if (!this.subQty || this.subQty < 1) {
      this.alertService.alert('Enter the substitute quantity', 'error');
      return;
    }
    this.addingLine = true;
    this.deliveryValidationService
      .addSubstituteLine(this.order.orderId, {
        orderItemId: opt.orderItemId,
        replaceLineId: opt.lineId,
        productVariantId: this.subProduct.productVariantId,
        quantity: this.subQty,
        replacedQty: opt.lineId ? this.replacedQty || 0 : 0,
        warehouse: this.subWarehouse,
        notes: this.subNotes.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.addingLine = false;
          this.showAddLine = false;
          this.alertService.alert('Substitute delivery line added', 'success');
          this.loadValidationRows(this.order);
        },
        error: (error) => {
          this.addingLine = false;
          this.alertService.alert(error?.error?.message || 'Failed to add delivery line', 'error');
        },
      });
  }

  confirmPendingSave() {
    const row = this.pendingConfirmation?.row;
    this.pendingConfirmation = null;
    if (row) this.doSave(row);
  }

  @HostListener('document:keydown.escape')
  dismissPendingSave() {
    this.pendingConfirmation = null;
    this.showAddLine = false;
  }

  private doSave(row: ValidationRow, onDone?: (ok: boolean) => void) {
    row.saving = true;
    this.deliveryValidationService
      .updateValidation(row.validation.id, {
        validatedQty: row.editCancel ? 0 : row.editValidatedQty,
        cancel: row.editCancel,
        condition: row.editCondition,
        notes: row.editNotes,
      })
      .subscribe({
        next: (updated) => {
          row.validation = updated;
          row.saving = false;
          row.justSaved = true;
          setTimeout(() => (row.justSaved = false), 1600);
          if (updated.scrapRecorded) {
            this.alertService.alert(`Recorded ${updated.scrapQuantity} in Stock Scrap`, 'success');
          }
          // Split lines were created server-side - pull the fresh set.
          if (updated.linesCreated) {
            this.loadValidationRows(this.order, () => onDone?.(true));
          } else {
            onDone?.(true);
          }
        },
        error: (error) => {
          row.saving = false;
          this.alertService.alert(error?.error?.message || 'Failed to save', 'error');
          onDone?.(false);
        },
      });
  }

  // Damaged/Partial/Cancel change stock or split the line, and Damaged/
  // Partial lines can't be edited afterwards - so spell out exactly what
  // will happen and ask first.
  private saveConfirmation(row: ValidationRow): SaveConfirmation | null {
    const expected = row.validation.expectedQty;
    const qty = row.editValidatedQty;
    const wasCancelled = row.validation.status === 'CANCELLED';
    const lockNote = { icon: 'lock', text: "This line can't be edited afterwards." };

    if (row.editCancel && !wasCancelled) {
      return {
        row, tone: 'neutral', icon: 'cancel', title: 'Cancel this line?', actionLabel: 'Cancel line',
        effects: [
          { icon: 'inventory_2', text: `${expected} unit${expected === 1 ? '' : 's'} will be added back to stock.` },
          { icon: 'block', text: 'Nothing on this line will be dispatched.' },
        ],
      };
    }
    if (!row.editCancel && wasCancelled) {
      return {
        row, tone: 'amber', icon: 'undo', title: 'Undo the cancel?', actionLabel: 'Undo cancel',
        effects: [{ icon: 'inventory_2', text: `${expected} unit${expected === 1 ? '' : 's'} will be taken off stock again.` }],
      };
    }
    if (row.editCancel) return null;
    if (row.editCondition === 'DAMAGED' && qty > 0 && qty <= expected) {
      return {
        row, tone: 'red', icon: 'warning', title: 'Save as damaged?', actionLabel: 'Save as damaged',
        effects: [
          { icon: 'inventory_2', text: `${qty} unit${qty === 1 ? '' : 's'} will be written off to Stock Scrap and taken off stock.` },
          { icon: 'add_box', text: `A replacement delivery line for ${qty} will be created.` },
          ...(qty < expected ? [{ icon: 'call_split', text: `The other ${expected - qty} move to a new pending line.` }] : []),
          lockNote,
        ],
      };
    }
    if (row.editCondition === 'PARTIAL' && qty > 0 && qty < expected) {
      return {
        row, tone: 'amber', icon: 'remove_circle', title: 'Save as partial?', actionLabel: 'Save as partial',
        effects: [
          { icon: 'local_shipping', text: `${qty} of ${expected} count as dispatched.` },
          { icon: 'call_split', text: `The remaining ${expected - qty} move to a new delivery line.` },
          lockNote,
        ],
      };
    }
    return null;
  }

  // Save is only meaningful when there's something to do: a not-yet-
  // checked line (saving validates it), or an already-saved line whose
  // quantity/condition/notes/cancel no longer match what's stored.
  rowDirty(row: ValidationRow): boolean {
    const v = row.validation;
    if (v.status === 'PENDING') return true;
    return (
      row.editValidatedQty !== (v.validatedQty ?? v.expectedQty) ||
      row.editCondition !== (v.condition ?? 'GOOD') ||
      row.editCancel !== (v.status === 'CANCELLED') ||
      (row.editNotes ?? '') !== (v.notes ?? '')
    );
  }

  // PARTIAL/DAMAGED lines have already split off follow-up lines and moved
  // stock, so like a closed-out order they're view-only.
  rowReadOnly(row: ValidationRow): boolean {
    return this.isReadOnly || row.validation.status === 'PARTIAL' || row.validation.status === 'DAMAGED';
  }

  get pendingCount(): number {
    return this.rows.filter((r) => r.validation.status === 'PENDING').length;
  }

  // A line that's done with: cancelled/split-off lines, or one saved in
  // Good condition. (Damaged/Partial lines have already spawned follow-up
  // lines that get checked in their own right.)
  private lineSettled(r: ValidationRow): boolean {
    return (
      ['CANCELLED', 'PARTIAL', 'DAMAGED'].includes(r.validation.status) ||
      (r.validation.status !== 'PENDING' && r.validation.condition === 'GOOD')
    );
  }

  // Ready to close out: every line settled, and at least one actually
  // dispatched (damaged/cancelled lines are settled but ship nothing).
  get canCloseOut(): boolean {
    return (
      this.rows.length > 0 &&
      this.rows.every((r) => this.lineSettled(r)) &&
      this.rows.some((r) => r.validation.status === 'VALIDATED' || r.validation.status === 'PARTIAL')
    );
  }

  // Would saving these lines (as they're currently filled in) leave the
  // order with nothing outstanding? Only a clean Good, full-quantity save
  // counts - and only while the order is still open.
  private wouldCloseOut(saving: ValidationRow[]): boolean {
    if (this.order?.orderStatus !== 'PROCESSING' || !saving.length) return false;
    const ids = new Set(saving.map((r) => r.validation.id));
    const cleanGood = saving.every(
      (r) => r.editCondition === 'GOOD' && !r.editCancel && r.editValidatedQty === r.validation.expectedQty,
    );
    if (!cleanGood) return false;
    return this.rows.every((r) => ids.has(r.validation.id) || this.lineSettled(r));
  }

  // The status choice comes *before* the save: the lines are only
  // validated if the order is closed out with one of the two statuses -
  // "Not now" saves nothing.
  private openCloseOutPrompt(rows: ValidationRow[]): void {
    this.closeOutRows = rows;
    this.statusNote = '';
    this.emailCustomer = true;
    this.selectedPartnerId = this.deliveryPartner?.id ?? null;
    this.showStatusPrompt = true;
    this.loadPartners();
  }

  // From the Order Status menu: same status choice + optional note, with
  // no lines to save first.
  openStatusPrompt(): void {
    this.openCloseOutPrompt([]);
  }

  get isCollection(): boolean {
    return this.order?.deliveryMethod === 'COLLECTION';
  }

  get partnerRequired(): boolean {
    return !this.isCollection;
  }

  get canConfirmCloseOut(): boolean {
    return !this.partnerRequired || this.selectedPartnerId != null;
  }

  private loadDeliveryPartner() {
    this.deliveryPartnersService.forOrder(this.orderId).subscribe({
      next: (p) => (this.deliveryPartner = p),
      error: () => (this.deliveryPartner = null), // table not set up yet - nothing to show
    });
  }

  private loadPartners() {
    if (this.partners.length || this.partnersLoading) return;
    this.partnersLoading = true;
    this.partnersError = '';
    this.deliveryPartnersService.list().subscribe({
      next: (rows) => {
        this.partners = rows || [];
        this.partnersLoading = false;
      },
      error: (e) => {
        this.partnersLoading = false;
        this.partnersError = e?.error?.message || 'Could not load delivery partners';
      },
    });
  }

  confirmStatusFromPrompt(value: string): void {
    if (!this.canConfirmCloseOut) {
      this.alertService.alert('Choose the delivery partner', 'error');
      return;
    }
    const partnerId = this.selectedPartnerId;
    // Save the partner first; nothing else happens if that fails.
    if (partnerId != null && partnerId !== this.deliveryPartner?.id) {
      this.deliveryPartnersService.assignToOrder(this.orderId, partnerId).subscribe({
        next: (p) => {
          this.deliveryPartner = p;
          this.closeOut(value);
        },
        error: (e) => this.alertService.alert(e?.error?.message || 'Could not save the delivery partner', 'error'),
      });
      return;
    }
    this.closeOut(value);
  }

  private closeOut(value: string): void {
    this.showStatusPrompt = false;
    const rows = this.closeOutRows;
    const note = this.statusNote;
    const email = this.emailCustomer;
    this.closeOutRows = [];
    this.statusNote = '';
    if (!rows.length) {
      this.updateOrderStatus(value, note, email);
      return;
    }
    let remaining = rows.length;
    let failed = false;
    rows.forEach((row) =>
      this.doSave(row, (ok) => {
        if (!ok) failed = true;
        remaining--;
        if (remaining === 0 && !failed) this.updateOrderStatus(value, note, email);
      }),
    );
  }

  dismissStatusPrompt(): void {
    this.showStatusPrompt = false;
    this.closeOutRows = [];
    this.statusNote = '';
  }

  // "Everything matches" shortcut - confirms every not-yet-validated row's
  // checked quantity equals what was ordered, one click instead of one per
  // row, since that's the common case (only exceptions need real attention).
  validateAllMatching() {
    const pending = this.rows.filter((r) => r.validation.status === 'PENDING');
    if (!pending.length) {
      this.alertService.alert('Nothing left to validate', 'success');
      return;
    }
    pending.forEach((row) => {
      row.editValidatedQty = row.validation.expectedQty;
      row.editCondition = 'GOOD';
    });
    if (this.wouldCloseOut(pending)) {
      this.openCloseOutPrompt(pending);
      return;
    }
    pending.forEach((row) => this.doSave(row));
  }

  backToQueue() {
    this.router.navigate(['/delivery-validation']);
  }

  // Packing/picking paperwork that travels with the goods - no prices or
  // amounts anywhere on it, just enough to check the right things are
  // going out: product, SKU, quantity ordered.
  printDeliverySlip() {
    if (!this.order) return;
    this.deliverySlipPdfService.print({
      orderNo: this.order.orderNo,
      orderDate: this.order.orderDate,
      deliveryMethod: this.order.deliveryMethod,
      shippingAddress: this.shippingAddress,
      // One slip line per delivery line: cancelled and written-off
      // (damaged) lines aren't in the box, and split lines carry their
      // own quantity.
      items: this.rows
        .filter((r) => r.validation.status !== 'CANCELLED' && r.validation.status !== 'DAMAGED')
        .map((r) => ({
          sku: this.rowProductSku(r),
          productName: this.rowProductName(r),
          quantity: r.validation.expectedQty,
          weight: r.validation.substituteVariantId ? r.dimensions?.weight : r.item.weight,
        })),
    });
  }
}
